import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonService } from '@src/app/services/common.service';
import { formatNumberWithPrefix, formatTinyNumber } from '@src/app/shared/utils/number.util';
import clsx from 'clsx';
import { NzModalService } from 'ng-zorro-antd/modal';
import { finalize } from 'rxjs';
import { MemeDashboardStatusService } from '../../meme-dashboard-status.service';
import { MemeDashboardService } from '../../meme-dashboard.service';
import { WatchlistAgentInfo } from '../../meme-dashboard.type';
import { ShareToSocialmediaModalComponent } from '../share-to-socialmedia-modal/share-to-socialmedia-modal.component';

@Component({
    selector: 'app-watchlist-agent-info',
    templateUrl: './watchlist-agent-info.component.html',
    styleUrls: ['./watchlist-agent-info.component.less'],
})
export class WatchlistAgentInfoComponent implements OnChanges {
    @Input() pageType: 'myWatchlist' | 'other' = 'other';
    @Input() agentInfo: WatchlistAgentInfo;

    formatNumberWithPrefix = formatNumberWithPrefix;
    formatTinyNumber = formatTinyNumber;

    metrics: string[] = ['Wallets Count', 'Subscribers', 'Subscription Earning', 'Daily Alerts', '7D Pnl', '30D Pnl', 'Win Rate', 'Daily Hunted MEMEs'];

    subscribable = true;

    changingPeopleCanSubscribe = false;

    unsubscribeModalVisible = false;

    get isMyWatchlist() {
        return this.pageType === 'myWatchlist';
    }

    get isOther() {
        return this.pageType === 'other';
    }

    submittingSubscribe = false;

    get openToSubscribeDisabled() {
        return this.changingPeopleCanSubscribe || !this.hasWatchlistWallets;
    }

    get hasWatchlistWallets() {
        return this.memeDashboardStatusService.myWatchlistWallets$.getValue()?.length;
    }

    get peopleCanSubscribeTooltip() {
        if (this.subscribable) return '';

        if (this.openToSubscribeDisabled) {
            return 'Please add wallets to your watchlist first.';
        }

        return "When set to 'can subscribe', your Watchlist becomes public to others.  You will be rewarded 80 points for each subscriber.";
    }

    constructor(
        private memeDashboardService: MemeDashboardService,
        private modalService: NzModalService,
        private memeDashboardStatusService: MemeDashboardStatusService,
        public commonService: CommonService
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['agentInfo']) {
            this.setupDataWhenAgentInfoUpdated();
        }
    }

    showEditAgentInfoModal() {
        this.memeDashboardService.showAddWatchlistDialog(true);
    }

    setupDataWhenAgentInfoUpdated() {
        this.subscribable = this.agentInfo?.allow_subscription;
    }

    changePeopleCanSubscribe() {
        if (!this.agentInfo?.name || !this.agentInfo?.logo_url) {
            setTimeout(() => {
                this.subscribable = !this.subscribable;
            }, 300);

            this.memeDashboardService.showAddWatchlistDialog(true);
            return;
        }

        this.changingPeopleCanSubscribe = true;

        if (this.subscribable) {
            this.memeDashboardService
                .createOrUpdateMyWatchListAgentInfo({ allow_subscription: true })
                .pipe(finalize(() => (this.changingPeopleCanSubscribe = false)))
                .subscribe({
                    next: data => {
                        this.memeDashboardStatusService.myWatchlistAgentInfo$.next(data);
                        const styles = clsx('text-center text-sm text-sub-text');
                        this.modalService.create({
                            nzTitle: 'Congrats! 🎉',
                            nzCentered: true,
                            nzContent: `<div class="${styles}">Now your Watchlist can be subscribed by others. Share it with friends——you will be rewarded 80 points for each subscriber. </div>`,
                            nzFooter: [
                                {
                                    label: 'Share to X',
                                    type: 'primary',
                                    onClick: () => this.showShareModal(),
                                },
                            ],
                        });
                    },
                    error: err => this.commonService.errorMessageByResponse(err),
                });
        } else {
            this.memeDashboardService
                .createOrUpdateMyWatchListAgentInfo({ allow_subscription: false })
                .pipe(finalize(() => (this.changingPeopleCanSubscribe = false)))
                .subscribe({
                    next: data => {
                        this.memeDashboardStatusService.myWatchlistAgentInfo$.next(data);
                    },
                    error: err => this.commonService.errorMessageByResponse(err),
                });
        }
    }

    subscribe() {
        this.submittingSubscribe = true;
        this.memeDashboardService
            .subscribeWatchlist(this.agentInfo.id)
            .pipe(finalize(() => (this.submittingSubscribe = false)))
            .subscribe({
                next: () => {
                    this.agentInfo.is_subscribed = true;
                    this.memeDashboardStatusService.fetchMySubscribedWatchlistAgents();

                    if (!this.memeDashboardStatusService.memeAgentInitialized) {
                        this.memeDashboardStatusService.showTGAgentSetupGuide('init_tg_agent');
                    }
                },
                error: err => {
                    if (err.body.code === 400) {
                        if (err.body.message.includes('You can only subscribe up to')) {
                            this.commonService.errorMessageByResponse(err);
                        } else {
                            this.memeDashboardStatusService.showInsufficientPointsModal(this.agentInfo);
                        }
                    } else {
                        this.commonService.errorMessageByResponse(err);
                    }
                },
            });
    }

    showShareModal() {
        this.modalService.create({
            nzContent: ShareToSocialmediaModalComponent,
            nzData: {
                agentInfo: this.agentInfo,
            },
            nzCentered: true,
            nzTitle: null,
            nzFooter: null,
            nzWidth: 'auto',
        });
    }

    unsubscribe() {
        this.submittingSubscribe = true;
        this.memeDashboardService
            .unsubscribeWatchlist(this.agentInfo.id)
            .pipe(finalize(() => (this.submittingSubscribe = false)))
            .subscribe({
                next: () => {
                    this.agentInfo.is_subscribed = false;

                    this.memeDashboardStatusService.mySubscribedWatchlistAgentIDs$.next(
                        this.memeDashboardStatusService.mySubscribedWatchlistAgentIDs$.getValue().filter(id => id !== this.agentInfo.id)
                    );

                    this.unsubscribeModalVisible = false;

                    this.commonService.success('Unsubscribed Successfully');
                },
                error: err => this.commonService.errorMessageByResponse(err),
            });
    }
}
